import { useEffect, useRef } from "react";

const SAMPLE_COUNT = 36;
const VIEW_HEIGHT = 36;
const MIN_AMP = 0.2;
const GLITCH_TICK_MS = 90;

const GLITCH_COLORS = [
	"#ea4e2c",
	"#e23722",
	"#00e5ff",
	"#ff2bd6",
	"#39ff14",
	"#ffe600",
	"#4d7cff",
	"#7cf5ff",
	"#ff6b4a",
];

function randomBetween(min: number, max: number) {
	return min + Math.random() * (max - min);
}

function pickColor(exclude?: string) {
	const pool = exclude
		? GLITCH_COLORS.filter((color) => color !== exclude)
		: GLITCH_COLORS;
	return pool[Math.floor(Math.random() * pool.length)];
}

type Ripple = {
	impactX: number;
	start: number;
	amplitude: number;
	duration: number;
	sigma: number;
	waveLength: number;
	phaseSpeed: number;
	direction: 1 | -1;
	colorA: string;
	colorB: string;
	ghostStrength: number;
};

type GlitchShift = {
	offsetAX: number;
	offsetAY: number;
	offsetBX: number;
	offsetBY: number;
	phaseA: number;
	phaseB: number;
	sliceA: { x: number; w: number; y: number };
	sliceB: { x: number; w: number; y: number };
};

function sampleY(
	x: number,
	midY: number,
	ripple: Pick<
		Ripple,
		"impactX" | "amplitude" | "sigma" | "waveLength" | "phaseSpeed" | "direction"
	>,
	elapsed: number,
	liveAmplitude: number,
	phaseExtra = 0,
) {
	const dx = x - ripple.impactX;
	const sigma = ripple.sigma + elapsed * 0.028;
	const envelope = Math.exp(-(dx * dx) / (2 * sigma * sigma));
	const phase = elapsed * ripple.phaseSpeed + phaseExtra;
	return (
		midY +
		ripple.direction *
			liveAmplitude *
			envelope *
			Math.sin((dx / ripple.waveLength) * Math.PI * 2 - phase)
	);
}

/** Catmull-Rom samples → cubic Bézier path for a round stroke. */
function buildPath(
	width: number,
	height: number,
	ripple: Ripple | null,
	elapsed: number,
	liveAmplitude: number,
	phaseExtra = 0,
	xShift = 0,
	yShift = 0,
) {
	const midY = height / 2;
	if (!ripple || liveAmplitude < MIN_AMP || width <= 0) {
		return `M 0 ${midY} L ${width} ${midY}`;
	}

	const points: { x: number; y: number }[] = [];
	for (let i = 0; i <= SAMPLE_COUNT; i += 1) {
		const x = (width * i) / SAMPLE_COUNT;
		points.push({
			x: x + xShift,
			y: sampleY(x, midY, ripple, elapsed, liveAmplitude, phaseExtra) + yShift,
		});
	}

	let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
	for (let i = 0; i < points.length - 1; i += 1) {
		const p0 = points[i === 0 ? 0 : i - 1];
		const p1 = points[i];
		const p2 = points[i + 1];
		const p3 = points[i + 2] ?? p2;
		const cp1x = p1.x + (p2.x - p0.x) / 6;
		const cp1y = p1.y + (p2.y - p0.y) / 6;
		const cp2x = p2.x - (p3.x - p1.x) / 6;
		const cp2y = p2.y - (p3.y - p1.y) / 6;
		d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
	}
	return d;
}

function buildSlice(slice: { x: number; w: number; y: number }) {
	return `M ${slice.x.toFixed(2)} ${slice.y.toFixed(2)} L ${(slice.x + slice.w).toFixed(2)} ${slice.y.toFixed(2)}`;
}

function nextShift(impactX: number, width: number, midY: number): GlitchShift {
	return {
		offsetAX: randomBetween(-9, -3),
		offsetAY: randomBetween(-3, 2),
		offsetBX: randomBetween(3, 9),
		offsetBY: randomBetween(-2, 3),
		phaseA: randomBetween(-0.8, 0.8),
		phaseB: randomBetween(-0.8, 0.8),
		sliceA: {
			x: Math.max(0, impactX + randomBetween(-70, -10)),
			w: randomBetween(28, 72),
			y: midY + randomBetween(-10, 10),
		},
		sliceB: {
			x: Math.min(width - 40, impactX + randomBetween(10, 70)),
			w: randomBetween(22, 58),
			y: midY + randomBetween(-10, 10),
		},
	};
}

export default function WaveDivider() {
	const rootRef = useRef<HTMLDivElement>(null);
	const pathRef = useRef<SVGPathElement>(null);
	const ghostARef = useRef<SVGPathElement>(null);
	const ghostBRef = useRef<SVGPathElement>(null);
	const sliceARef = useRef<SVGPathElement>(null);
	const sliceBRef = useRef<SVGPathElement>(null);
	const widthRef = useRef(0);
	const lastYRef = useRef<number | null>(null);
	const rippleRef = useRef<Ripple | null>(null);
	const shiftRef = useRef<GlitchShift | null>(null);
	const rafRef = useRef(0);
	const tickRef = useRef(0);

	useEffect(() => {
		const root = rootRef.current;
		const path = pathRef.current;
		const ghostA = ghostARef.current;
		const ghostB = ghostBRef.current;
		const sliceA = sliceARef.current;
		const sliceB = sliceBRef.current;
		if (!root || !path || !ghostA || !ghostB || !sliceA || !sliceB) return;

		const reduceMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const finePointer = window.matchMedia("(pointer: fine)").matches;

		const hideGhosts = () => {
			for (const node of [ghostA, ghostB, sliceA, sliceB]) {
				node.setAttribute("d", "");
				node.style.opacity = "0";
			}
			root.classList.remove("is-glitching");
		};

		const paintFlat = () => {
			path.setAttribute(
				"d",
				buildPath(widthRef.current, VIEW_HEIGHT, null, 0, 0),
			);
			hideGhosts();
		};

		const syncSize = () => {
			widthRef.current = root.clientWidth;
			if (!rippleRef.current) paintFlat();
		};

		const draw = (now: number) => {
			const ripple = rippleRef.current;
			const width = widthRef.current;
			if (!ripple) {
				paintFlat();
				return;
			}

			const elapsed = now - ripple.start;
			const progress = Math.min(1, elapsed / ripple.duration);
			const ease = (1 - progress) ** 2;
			const amplitude = ripple.amplitude * ease;
			const midY = VIEW_HEIGHT / 2;
			const shift =
				shiftRef.current ?? nextShift(ripple.impactX, width, midY);
			shiftRef.current = shift;

			path.setAttribute(
				"d",
				buildPath(width, VIEW_HEIGHT, ripple, elapsed, amplitude),
			);

			const ghostOpacity = Math.min(0.85, ease * 1.15);
			ghostA.style.opacity = String(ghostOpacity);
			ghostB.style.opacity = String(ghostOpacity);
			sliceA.style.opacity = String(ghostOpacity * 0.9);
			sliceB.style.opacity = String(ghostOpacity * 0.9);
			ghostA.style.stroke = ripple.colorA;
			ghostB.style.stroke = ripple.colorB;
			sliceA.style.stroke = ripple.colorA;
			sliceB.style.stroke = ripple.colorB;

			const ghostAmplitude = amplitude * ripple.ghostStrength;
			ghostA.setAttribute(
				"d",
				buildPath(
					width,
					VIEW_HEIGHT,
					ripple,
					elapsed,
					ghostAmplitude,
					shift.phaseA,
					shift.offsetAX,
					shift.offsetAY,
				),
			);
			ghostB.setAttribute(
				"d",
				buildPath(
					width,
					VIEW_HEIGHT,
					ripple,
					elapsed,
					ghostAmplitude,
					shift.phaseB,
					shift.offsetBX,
					shift.offsetBY,
				),
			);
			sliceA.setAttribute("d", buildSlice(shift.sliceA));
			sliceB.setAttribute("d", buildSlice(shift.sliceB));

			if (progress >= 1 || amplitude < MIN_AMP) {
				rippleRef.current = null;
				shiftRef.current = null;
				window.clearInterval(tickRef.current);
				tickRef.current = 0;
				paintFlat();
				return;
			}

			rafRef.current = window.requestAnimationFrame(draw);
		};

		const spawn = (impactX: number) => {
			if (reduceMotion || !finePointer) return;
			if (rippleRef.current) return;

			const colorA = pickColor();
			const colorB = pickColor(colorA);
			const clampedX = Math.max(0, Math.min(widthRef.current, impactX));
			rippleRef.current = {
				impactX: clampedX,
				start: performance.now(),
				amplitude: randomBetween(7, 16),
				duration: randomBetween(520, 1100),
				sigma: randomBetween(48, 78),
				waveLength: randomBetween(95, 160),
				phaseSpeed: randomBetween(0.01, 0.022),
				direction: Math.random() < 0.5 ? 1 : -1,
				colorA,
				colorB,
				ghostStrength: randomBetween(2, 3),
			};
			shiftRef.current = nextShift(
				clampedX,
				widthRef.current,
				VIEW_HEIGHT / 2,
			);
			root.classList.add("is-glitching");
			window.clearInterval(tickRef.current);
			tickRef.current = window.setInterval(() => {
				const active = rippleRef.current;
				if (!active) return;
				shiftRef.current = nextShift(
					active.impactX,
					widthRef.current,
					VIEW_HEIGHT / 2,
				);
			}, GLITCH_TICK_MS);
			rafRef.current = window.requestAnimationFrame(draw);
		};

		const onPointerMove = (event: PointerEvent) => {
			if (event.pointerType !== "mouse") return;
			const rect = root.getBoundingClientRect();
			const mid = rect.top + rect.height / 2;
			const y = event.clientY;
			const prev = lastYRef.current;
			lastYRef.current = y;

			if (prev == null) return;
			const crossed = (prev < mid && y >= mid) || (prev > mid && y <= mid);
			const inX = event.clientX >= rect.left && event.clientX <= rect.right;
			if (crossed && inX) spawn(event.clientX - rect.left);
		};

		syncSize();
		const observer = new ResizeObserver(syncSize);
		observer.observe(root);

		if (!reduceMotion && finePointer) {
			window.addEventListener("pointermove", onPointerMove, {
				passive: true,
			});
		}

		return () => {
			observer.disconnect();
			window.removeEventListener("pointermove", onPointerMove);
			window.cancelAnimationFrame(rafRef.current);
			window.clearInterval(tickRef.current);
		};
	}, []);

	return (
		<div ref={rootRef} className="wave-divider" aria-hidden="true">
			<svg
				className="wave-divider-svg"
				width="100%"
				height={VIEW_HEIGHT}
				preserveAspectRatio="none"
			>
				<path ref={ghostARef} className="wave-divider-ghost wave-divider-ghost-a" />
				<path ref={ghostBRef} className="wave-divider-ghost wave-divider-ghost-b" />
				<path ref={sliceARef} className="wave-divider-slice" />
				<path ref={sliceBRef} className="wave-divider-slice" />
				<path ref={pathRef} className="wave-divider-path" />
			</svg>
		</div>
	);
}
